import { Box, Button, Stack } from "@mui/material";
import {
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonEditLink,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRemoveFormatting,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuDivider,
} from "mui-tiptap";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import HtmlEditor from "@entrilia/shared/components/html/HtmlEditor";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type { Note } from "../../api/types/notesTypes";
import { useNotesContext } from "./NotesContext";

const createNote = withErrorHandling(adminApi.createNote);
const updateNote = withErrorHandling(adminApi.updateNote);

const HtmlEditorMenuControls = () => (
  <MenuControlsContainer>
    <MenuButtonBold />
    <MenuButtonItalic />
    <MenuButtonUnderline />
    <MenuDivider />
    <MenuButtonOrderedList />
    <MenuButtonBulletedList />
    <MenuDivider />
    <MenuButtonEditLink />
    <MenuDivider />
    <MenuButtonRemoveFormatting />
  </MenuControlsContainer>
);

interface Props {
  id?: string;
  initialContent: string;
  onSaved: (id: string | undefined, note: Note) => void;
  onCancel: () => void;
}

const NoteEditor = ({ id, initialContent, onSaved, onCancel }: Props) => {
  const { entityType, entityId, objectType } = useNotesContext();
  const { sendNotificationError } = useNotificationContext();
  const [htmlContent, setHtmlContent] = useState<string>(initialContent);
  const [saving, setSaving] = useState(false);

  const handleSaveClick = async () => {
    setSaving(true);

    const isNewNote = !id;
    const [savedNote, error] = isNewNote
      ? await createNote(entityType, entityId, { htmlUserInput: htmlContent }, objectType)
      : await updateNote(entityType, entityId, id, { htmlUserInput: htmlContent }, objectType);

    setSaving(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[NoteEditor] saving note");
      return;
    }

    onSaved(id, savedNote);
  };

  const isSaveDisabled = initialContent.trim() === htmlContent.trim();

  return (
    <Box sx={{ py: 1 }}>
      <HtmlEditor
        initialContent={initialContent}
        onChange={setHtmlContent}
        menuControls={HtmlEditorMenuControls}
        focusOnInit
        editorStyles={{ minHeight: "7rem" }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button loading={saving} disabled={isSaveDisabled} variant="contained" onClick={handleSaveClick}>
          Save
        </Button>
        <Button variant="text" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

export default NoteEditor;
